import React from 'react';
import PropTypes from 'prop-types';
import { Howl } from 'howler';
import { isIOS15Safari } from '../utils/browserUtils';
import { ApolloContext } from '../../ApolloContextProvider';
import ASSESSMENT_CAPTIONS from '../../captions/captionsConstants/captions';

export default class AudioView extends React.Component {
    constructor(props) {
        super(props);
        this.initPlaylist = this.initPlaylist.bind(this);
        this.startPlay = this.startPlay.bind(this);
        this.displaySubtitles = this.displaySubtitles.bind(this);
        this.state = {
            captionsContent: '',
            startedPlaying: false,
        };
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.isPaused && this.props.isReadyToPlay && this.props.audioTracks !== prevProps.audioTracks ) {
            this.initPlaylist(this.props);
        }
        if (this.props.isReadyToPlay) {
            if (prevProps.isPaused && !this.props.isPaused) {
                this.startPlay();
            } else if (!this.state.startedPlaying)  {
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ startedPlaying: true });
                setTimeout(this.startPlay, this.props.delay);
            }
        }
        if (!prevProps.isPaused && this.props.isPaused) {
            this.pausePlay();
        }
    }

    componentWillUnmount() {
        this.destroyPlaylist();
    }

    isSoundPlaying() {
        return this.playlist.length > 0 && this.playlist[this.currentTrack].playing();
    }

    displaySubtitles(sound, audioTrack) {
        const captions = ASSESSMENT_CAPTIONS[this.props.assessment.name][audioTrack];
        if (!captions) return;
        // If the sound is playing, find in which second it is currently playing
        // Otherwise exit from a function
        if (!this.isSoundPlaying()) return;
        const currentTime = sound.seek();

        let subtitleToShow = null;
        // Find the subtitle that should be displayed at the current time
        if (captions.length) {
            for (let i = 0; i < captions.length; i += 1) {
                if (currentTime >= captions[i].start && currentTime < captions[i].end) {
                    subtitleToShow = captions[i];
                    break;
                }
            }
        }
        // Display the subtitle content in the captions container
        if (subtitleToShow) {
            if (subtitleToShow.content !== this.state.captionsContent) {
                this.setState({ captionsContent: subtitleToShow.content });
            }
        }
        else {
            this.setState({captionsContent: ''});
        }
        // Repeat this function every 100 milliseconds to update the captions if the sound is playing
        setTimeout(this.displaySubtitles, 100, sound, audioTrack);
    }

    initPlaylist(props) {
        this.destroyPlaylist();
        if (props.audioTracks.length === 0) {
            return;
        }
        this.playlist = [];
        // This parameter contains information
        // if mp3 file has been preloaded or not
        // If it was, we do not invoke unload
        // on playlist destroy
        // (needed for MAZE offline)
        this.preloadStatuses = [];
        this.currentTrack = 0;
        const playNextTrack = () => {
            // Set current audio to the beginning
            // (needed for MAZE offline)
            this.playlist[this.currentTrack].stop();
            this.currentTrack += 1;
            if (this.currentTrack === this.playlist.length) {
                this.endPlay();
                if (props.onEndCallback) {
                    props.onEndCallback(this.currentTrack);
                }
                return;
            }
            setTimeout(() => {
                this.playlist[this.currentTrack].play();
            }, props.delay);
        };
        props.audioTracks.forEach((current, i) => {
            let sound;
            // The following code was added to support audio caching,
            // which is necessary to enable offline mode in the SEB browser.
            // If a user enters the SEB browser application,
            // it creates Howl objects for MAZE instructions
            // upon application load. The SEB browser does not support
            // service workers, so storing audio in memory is an
            // alternative solution.
            if (props.audioTracks[i] instanceof Howl) {
                sound = props.audioTracks[i];
                sound.on('end', playNextTrack);
                sound.on('play', () => {
                    if (props.onPlayCallback) {
                        props.onPlayCallback(this.currentTrack);
                    }
                    this.setState({ startedPlaying: true });
                    if (this.context.isCaptionsEnabled) {
                        // eslint-disable-next-line no-underscore-dangle
                        this.displaySubtitles(sound, props.audioTracks[i]._src);
                    }
                });
                sound.on('loaderror', () => {
                    this.props.onAudioLoadingError();
                });
                sound.on('playerror', () => {
                    sound.once('unlock', () => {
                        sound.play();
                    });
                });
                this.preloadStatuses.push(true);
            } else {
                sound = new Howl({
                    src: props.audioTracks[i],
                    onend: playNextTrack,
                    onplay: () => {
                        if (props.onPlayCallback) {
                            props.onPlayCallback(this.currentTrack);
                        }
                        this.setState({ startedPlaying: true });
                        if (this.context.isCaptionsEnabled) {
                            this.displaySubtitles(sound, props.audioTracks[i]);
                        }
                    },
                    onloaderror: () => {
                        this.props.onAudioLoadingError();
                    },
                    onplayerror: () => {
                        if (isIOS15Safari) {
                            sound.once('unlock', () => {
                                sound.play();
                            });
                        }
                    },
                    buffer: true,
                });
                this.preloadStatuses.push(false);
            }
            this.playlist.push(sound);
        });
    }

    startPlay() {
        if (this.playlist) {
            this.playlist[0].play();
        }
    }

    pausePlay() {
        if (this.playlist) {
            this.playlist[0].pause();
        }
    }

    endPlay() {
        this.currentTrack = 0;
        this.props.stopAudio();
        this.setState({captionsContent: ''});
    }

    destroyPlaylist() {
        if (this.playlist) {
            this.playlist.forEach((_, i) => {
                this.playlist[i].off();
                this.playlist[i].stop();
                if (!this.preloadStatuses[i]) {
                    this.playlist[i].unload();
                }
                this.playlist[i] = null;
            });
            this.playlist = null;
            this.preloadStatuses = null;
            this.currentTrack = 0;
            this.setState({ startedPlaying: false });
        }
    }

    render() {
        return this.props.isReadyToPlay && !this.props.isPaused &&
            this.context.isCaptionsEnabled && this.state.captionsContent !== '' ? (
                <div className="captions-container">
                    <span className="captions">{this.state.captionsContent}</span>
                </div>
            ) : null;
    }
}

AudioView.propTypes = {
    stopAudio: PropTypes.func,
    onAudioLoadingError: PropTypes.func,
    /* eslint-disable no-unused-vars, react/no-unused-prop-types */
    /* These are props that are never referenced as `this.props` so they trigger lint errors */
    audioTracks: PropTypes.array.isRequired,
    delay: PropTypes.number,
    isReadyToPlay: PropTypes.bool.isRequired,
    isPaused: PropTypes.bool.isRequired,
    onPlayCallback: PropTypes.func,
    onEndCallback: PropTypes.func,
    isCaptionsVisible: PropTypes.bool,
    assessment: PropTypes.object,
    /* eslint-enable no-unused-vars, react/no-unused-prop-types */
};

AudioView.defaultProps = {
    onAudioLoadingError: () => {
    },
    delay: 0,
    onPlayCallback: undefined,
    onEndCallback: undefined,
    stopAudio: () => {
    },
    isCaptionsVisible: false,
    assessment: {},
};

AudioView.contextType = ApolloContext;
